import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onIdTokenChanged, signOut, type User } from 'firebase/auth';
import { auth } from '../services/firebaseConfig';
import { finishSsoRedirect, getPendingSsoRedirect } from '../lib/auth';
import { clearSentryUser, setSentryUser } from '../lib/sentry';
import { readApiError } from '../lib/apiError';

export interface AuthSession {
  user: User;
  uid: string;
  email: string | null;
  displayName: string | null;
  activeOrgId: string | null;
  orgRoles: string[];
  platformRoles: string[];
  gcipTenantId: string | null;
}

interface AuthContextValue {
  loading: boolean;
  session: AuthSession | null;
  signOut: () => Promise<void>;
  refreshClaims: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  /** Set once an SSO redirect sign-in finishes on this page load, carrying
   *  the page the user was opening before sign-in (unvalidated — callers
   *  run it through lib/returnTo). The sign-in page consumes it to
   *  navigate on, then clears it. */
  ssoCompletion: { returnTo: string | null } | null;
  clearSsoCompletion: () => void;
}

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

const AuthContext = createContext<AuthContextValue | null>(null);

async function buildSession(user: User): Promise<AuthSession> {
  const tokenResult = await user.getIdTokenResult();
  const claims = tokenResult.claims as {
    activeOrgId?: string;
    orgRoles?: string[];
    platformRoles?: string[];
    firebase?: { tenant?: string };
  };
  return {
    user,
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    activeOrgId: claims.activeOrgId ?? null,
    orgRoles: claims.orgRoles ?? [],
    platformRoles: claims.platformRoles ?? [],
    gcipTenantId: claims.firebase?.tenant ?? null,
  };
}

/** True when nothing a consumer keys on has changed, so a routine token
 *  refresh keeps the same session object and no effect re-runs. */
function sameSession(a: AuthSession, b: AuthSession): boolean {
  return (
    a.user === b.user &&
    a.uid === b.uid &&
    a.email === b.email &&
    a.displayName === b.displayName &&
    a.activeOrgId === b.activeOrgId &&
    a.gcipTenantId === b.gcipTenantId &&
    a.orgRoles.length === b.orgRoles.length &&
    a.orgRoles.every((r, i) => r === b.orgRoles[i]) &&
    a.platformRoles.length === b.platformRoles.length &&
    a.platformRoles.every((r, i) => r === b.platformRoles[i])
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [ssoCompletion, setSsoCompletion] = useState<{ returnTo: string | null } | null>(null);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    // If we're returning from an SSO redirect, mint claims BEFORE letting
    // onAuthStateChanged build a session — otherwise the user would
    // momentarily have a Firebase user with no orgRoles, which the route
    // guards interpret as "no membership" and bounce them.
    const pending = getPendingSsoRedirect();
    if (pending) {
      finishSsoRedirect()
        .then((result) => {
          if (result) setSsoCompletion({ returnTo: pending.returnTo ?? null });
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('SSO redirect completion failed', err);
        });
    }

    // onIdTokenChanged, NOT onAuthStateChanged. The first beta tester
    // (2026-09-20) signed in, minted claims, landed on the launcher and
    // saw "No apps entitled" with no app menu: onAuthStateChanged had
    // built the session from the sign-in token BEFORE /v1/auth/claims
    // attached activeOrgId, and the forced token refresh that followed
    // does not fire onAuthStateChanged, so the session kept
    // activeOrgId=null until a full reload. Returning users never hit
    // it — their claims persist on the account from the first mint —
    // which is exactly why it went unnoticed. onIdTokenChanged fires
    // on sign-in, sign-out AND every token refresh; the session is
    // rebuilt only when a claim actually changed, so the hourly
    // refresh does not churn consumers.
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setSession(null);
        // MP135 G2 — Drop the user/org tags from any subsequent
        // Sentry events so post-signout errors aren't attributed to
        // the previous tenant.
        clearSentryUser();
        setLoading(false);
        return;
      }
      const s = await buildSession(user);
      setSession((prev) => (prev && sameSession(prev, s) ? prev : s));
      // MP135 G2 — Attach uid + active org as Sentry scope so the
      // dashboard groups errors by user / org for triage.
      setSentryUser({ uid: s.uid, orgId: s.activeOrgId });
      setLoading(false);
    });
    return unsub;
  }, []);

  const value: AuthContextValue = {
    loading,
    session,
    ssoCompletion,
    clearSsoCompletion: () => setSsoCompletion(null),
    signOut: async () => {
      if (!auth) return;
      await signOut(auth);
    },
    refreshClaims: async () => {
      if (!session) return;
      await session.user.getIdToken(true);
      const s = await buildSession(session.user);
      setSession(s);
      setSentryUser({ uid: s.uid, orgId: s.activeOrgId });
    },
    switchOrg: async (orgId: string) => {
      if (!session) throw new Error('No active session');
      const idToken = await session.user.getIdToken();
      const res = await fetch(`${API_URL}/v1/auth/claims`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ preferredOrgId: orgId }),
      });
      if (!res.ok) throw await readApiError(res);
      await session.user.getIdToken(true);
      const s = await buildSession(session.user);
      setSession(s);
      setSentryUser({ uid: s.uid, orgId: s.activeOrgId });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
